<template lang="pug">
.sidecart.js-sidecart(data-sidecart v-on:carttoggle="cartDisplay")
  transition(name="fade")
    .sidecart__overlay(v-if="sidecartOpen", @click="cartDisplay")
  transition(name="slide")
    .sidecart__drawer(v-if="sidecartOpen", data-sidecart-visible, role="dialog", aria-modal="true", aria-label="Your Cart")
      .sidecart__header
        .sidecart__header-content
          h2.sidecart__title(ref="sidecartHeading" :tabindex="0") {{ heading }}
            small &nbsp;{{ cartCountText }}
          button.button--icon.sidecart__close(@click="$store.dispatch('cart/closeSidecart')")
            .u-sr-only Close side cart
            icon(name="close" height="20px" width="20px" fill="#003a54")
      .sidecart__container
        transition(name="fade")
          .sidecart__contents-wrapper(v-if="cartData.item_count > 0", key="content")
            .sidecart__contents
              CartLineItem(
                v-for="(item, index) in cartData.items"
                :item="item"
                :line="index + 1"
                :key="index"
                :name="index + 1")
            .sidecart__footer
              .sidecart__subtotal.h5
                span Subtotal:
                span.subtotal__amount {{getCartTotal | moneyWithoutDecimals}}
              a.btn.btn--primary.sidecart__checkout-cta(:full="true" href="/checkout") Checkout
          .sidecart__empty(v-else key="empty")
            p.sidecart__empty-text( :tabindex="0" ) {{ emptyText }}
</template>

<script>
  import Icon from 'scripts/components/basic/Icon.vue';
import CartLineItem from 'scripts/components/cart/CartLineItem.vue';
import { mapState } from 'vuex';

  export default {
    name: 'Sidecart',
    components: { Icon, CartLineItem },
    props: {
      heading: String,
      emptyText: String,
      giftProducts: {
        priceThreshold: Number,
        isNewCustomer: Boolean,
        newCustomersGift: Object,
        currentCustomersGift: Object
      }
    },
    computed: {
      ...mapState({
        sidecartOpen: (state) => state.cart.sidecartOpen,
        cartData: (state) => state.cart.checkout,
        items: (state) => state.cart.checkout.items,
        cartTotal: (state) => state.cart.checkout.total_price,
      }),
      getCartTotal() {
        if(this.giftProductAvailable && this.cartHasGiftProduct) {
          const getGiftProduct = this.items.filter((item) => item.id == this.giftProductAvailable)
          return this.cartTotal - getGiftProduct[0].final_price
        } else {
          return this.cartTotal
        }
      },
      cartCountText() {
        return this.cartData.item_count < 1 ? '' : `(${this.cartData.item_count})`;
      },
      isNewCustomersGiftAvailable() {
        return (this.giftProducts.newCustomersGift !== undefined) ? this.giftProducts.newCustomersGift : false
      },
      isCurrentCustomersGiftAvailable() {
        return (this.giftProducts.newCustomersGift !== undefined) ? this.giftProducts.currentCustomersGift : false
      },
      hasMetThreshold() {
        return ((this.getCartTotal / 100) > this.giftProducts.priceThreshold) ? true : false
      },
      giftProductAvailable() {
        if(!this.giftProducts.isNewCustomer && this.isNewCustomersGiftAvailable) {
          return this.giftProducts.newCustomersGift.variants[0].id
        } else if(this.giftProducts.isNewCustomer && this.isCurrentCustomersGiftAvailable) {
          return this.giftProducts.currentCustomersGift.variants[0].id
        } else {
          return  false
        }
      },
      cartHasGiftProduct() {
        return this.items.some((item) => item.properties && item.properties.threshold === 'threshold')
      },
    },
    watch: {
      sidecartOpen() {
        this.$nextTick(() => {
          document.querySelector('.sidecart .sidecart__close').focus();
        });
      },
      cartTotal() {
        this.checkGiftWithPurchase();
      },
    },
    methods: {
      cartDisplay() {
        if (this.sidecartOpen) {
          this.$store.dispatch('cart/closeSidecart')
        } else {
          this.$store.dispatch('cart/openSidecart')
        }
      },
      checkGiftWithPurchase() {
        if (this.hasMetThreshold) {
          if (this.giftProductAvailable && !this.cartHasGiftProduct) {
            this.addGiftProduct();
          }
        } else if(this.cartHasGiftProduct) {
          this.removeGiftProduct();
        }
      },
      addGiftProduct() {
        this.$store.dispatch('cart/addItem', {
          quantity: 1,
          id: this.giftProductAvailable,
          properties: {
            threshold: 'threshold',
            thresholdPrice: this.giftProducts.priceThreshold
          },
        });
      },
      removeGiftProduct() {
        const line = this.items.findIndex((item) => {
          return item.id === this.giftProductAvailable
        }) + 1;
        this.$store.dispatch('cart/removeItem', line)
      },
    }
  };
</script>

<style scoped lang="scss">
  .sidecart {}
  .sidecart__overlay {
    position: fixed;
    top: 0;
    right: 0;
    z-index: 9999;
    width: 100%;
    height: 100%;
    background-color: $color-transparent-black;
    cursor: close;
  }

  .sidecart__drawer {
    display: flex;
    position: fixed;
    top: 0;
    right: 0;
    z-index: 10000;
    width: 100%;
    height: 100%;
    flex-direction: column;
    background-color: $color-white;
    margin-right: -480px;
    @include tablet-up {
      border-left: 1px solid $color-black;
      max-width: 480px;
    }
    &[data-sidecart-visible] {
      margin-right: 0;
    }
  }

  .sidecart__header {
    position: relative;
    flex-shrink: 0;
  }

  .sidecart__header-background {
    padding-bottom: 27.5%;
    height: 0;
    background-size: 105%;
    background-repeat: no-repeat;
    background-position: top left;
  }

  .sidecart__close {
    background: transparent;
    border: 0;
    padding: 0;
    min-width: auto;
  }

  .sidecart__header-content {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 16px;
    width: 100%;
    border-bottom: 1px solid $color-black;
  }

  .sidecart__title {
    margin: 0;
    font-size: rem(23);
    line-height: rem(29);
    text-align: center;
    display: flex;
    align-items: center;
    color: $color-slate-500;

    @include tablet-up {
      font-size: rem(20);
    }

    &:focus {
      box-shadow: none;
    }
  }

  .sidecart__count {
    font-size: rem(14);
    line-height: rem(20);

    @include tablet-up {
      font-size: rem(16);
    }
  }

  .sidecart__container {
    display: flex;
    flex-grow: 1;
    min-height: 0; /* [1] */
  }

  .sidecart__contents-wrapper {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    flex-grow: 1;
  }

  .sidecart__contents {
    flex-grow: 1;
    padding: 0 20px 20px;
    overflow-y: auto;
    overflow-x: hidden;
    &::-webkit-scrollbar {
      width: 10px;
    }
    &::-webkit-scrollbar-thumb {
      background-color: var(--color-navy-500);
    }
    &::-webkit-scrollbar-track {
      background-color: $color-black;
    }
  }

  .sidecart__checkout-cta {
    width: 100%;
  }

  .sidecart__empty {
    display: flex;
    padding: 50px;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 100%;
    background-size: 181px;
    background-repeat: no-repeat;
    background-position: bottom right;

    @include tablet-up {
      background-size: 251px;
    }
  }

  .sidecart__empty-text {
    margin-bottom: 50px;
    color: $color-white;
    font-size: rem(23);
    line-height: rem(29);
    text-align: center;

    @include tablet-up {
      font-size: rem(24);
      line-height: rem(28);
    }
  }

  .sidecart__footer {
    flex-shrink: 0;
    padding: 15px 20px;
    border-top: 1px solid $color-black;
  }

  .sidecart__subtotal {
    color: $color-navy-500;
    width: 100%;
    margin-bottom: 15px;
    .subtotal__amount {
      float: right;
    }
  }

  .u-sr-only {
    border: 0;
    clip: rect(0 0 0 0);
    height: auto;
    margin: 0;
    overflow: hidden;
    padding: 0;
    position: absolute;
    width: 1px;
    white-space: nowrap;
  }

  // Transitions
  .slide-leave-active,
  .slide-enter-active {
    transition: .5s;
    @include tablet-up {
      transition: 1s;
    }
  }
  .slide-enter {
    transform: translate(100%, 0);
  }
  .slide-leave-to {
    transform: translate(100%, 0);
  }
</style>
